import { ChevronDownIcon } from "@heroicons/react/24/outline"
import React from "react"
import { useState, useEffect } from "react"
import { useMutation } from "urql"

import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import { Toggle } from "../../../../components/shared/Inputs"
import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"

import EditorFooter from "./shared/EditorFooter"

// ColorInput component (not currently used but kept for future use)
// const ColorInput = ({ label, value, onChange, className = "" }) => {
//   const inputId = `color-input-${label.toLowerCase().replace(/\s+/g, "-")}`
//   return (
//     <div className={`space-y-1 ${className}`}>
//       <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
//         {label}
//       </label>
//       <div className="flex gap-2">
//         <input
//           id={inputId}
//           type="color"
//           value={value || "#ffffff"}
//           onChange={(e) => onChange(e.target.value)}
//           className="h-8 w-12 cursor-pointer rounded border border-gray-light"
//         />
//         <Input
//           value={value || ""}
//           onChange={(e) => onChange(e.target.value)}
//           placeholder="#ffffff"
//           className="flex-1"
//         />
//       </div>
//     </div>
//   )
// }

const layoutOptions = [
  {
    id: "scattered_images",
    name: "Scattered Images",
    description: "Multiple images arranged in a scattered layout",
    image: require("../../../../../assets/images/builder/v2/layout-images/scattered-imgs.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/scattered-imgs-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/scattered-imgs-selected.png")
  },
  {
    id: "full_image",
    name: "Full Image",
    description: "Full width background image with centered text overlay",
    image: require("../../../../../assets/images/builder/v2/layout-images/full-img.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/full-img-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/full-img-selected.png")
  },
  {
    id: "contained_image",
    name: "Contained Image",
    description: "Full width contained image with centered text overlay",
    image: require("../../../../../assets/images/builder/v2/layout-images/contained-img.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/contained-img-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/contained-img-selected.png")
  },
  {
    id: "overlap_double_image",
    name: "Overlap double image",
    description: "Two images overlapping with centered content",
    image: require("../../../../../assets/images/builder/v2/layout-images/overlap-double-img.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/overlap-double-img-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/overlap-double-img-selected.png")
  },
  {
    id: "image_left",
    name: "Image Left",
    description: "Image on the left, text on the right",
    image: require("../../../../../assets/images/builder/v2/layout-images/img-l.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-l-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-l-selected.png")
  },
  {
    id: "image_right",
    name: "Image Right",
    description: "Image on the right, text on the left",
    image: require("../../../../../assets/images/builder/v2/layout-images/img-r.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-r-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-r-selected.png")
  },
  {
    id: "half_image_left",
    name: "Half Image Left",
    description: "Half-width image on the left with text overlay",
    image: require("../../../../../assets/images/builder/v2/layout-images/half-img-l.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/half-img-l-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/half-img-l-selected.png")
  },
  {
    id: "half_image_right",
    name: "Half Image Right",
    description: "Half-width image on the right with text overlay",
    image: require("../../../../../assets/images/builder/v2/layout-images/half-img-r.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/half-img-r-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/half-img-r-selected.png")
  }
]

const LayoutOption = ({ option, isSelected, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false)

  const getImageSource = () => {
    if (isSelected) {
      return option.imageSelected
    } else if (isHovered) {
      return option.imageHover
    } else {
      return option.image
    }
  }

  return (
    <div
      className="cursor-pointer transition-all"
      onClick={() => onSelect(option.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={option.name}>
      <img
        src={getImageSource()}
        alt={`${option.name} layout`}
        className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
        onError={(e) => {
          e.target.style.display = "none"
          e.target.nextSibling.style.display = "block"
        }}
      />
      <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
        <div className="text-xs text-gray-500">{option.name}</div>
      </div>
    </div>
  )
}

const SubHeroEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()

  const [formData, setFormData] = useState({
    headline: section?.headline || practice?.websiteHeadline || practice?.headline || "",
    subheadline: section?.subheadline || practice?.subHeadline || "",
    body: section?.body || "",
    imageUrl: section?.imageUrl || practice?.headerHeroImageUrl || "",
    settings: section?.settings || {
      layout: "image_right",
      textAlign: "left",
      headingAlign: "left",
      subheadingAlign: "left",
      ctaAlign: "left",
      overlayOpacity: 30,
      showCTA: true,
      ctaText: "Book a Discovery Call",
      ctaAction: "service",
      ctaTarget: "",
      showReviews: true,
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      images: [],
      // Font settings
      headlineSize: "56px",
      headlineColor: "#0b3954",
      subheadlineSize: "20px",
      subheadlineColor: "#55778a",
      // CTA button spacing
      ctaTopMargin: 24
    }
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [errors, setErrors] = useState({})

  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || practice?.websiteHeadline || practice?.headline || "",
        subheadline: section.subheadline || practice?.subHeadline || "",
        body: section.body || "",
        imageUrl: section.imageUrl || practice?.headerHeroImageUrl || "",
        settings: {
          layout: "image_right",
          textAlign: "left",
          headingAlign: "left",
          subheadingAlign: "left",
          ctaAlign: "left",
          overlayOpacity: 30,
          showCTA: true,
          ctaText: "Book a Discovery Call",
          ctaAction: "service",
          ctaTarget: "",
          showReviews: true,
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          images: [],
          // Font settings
          headlineSize: "56px",
          headlineColor: "#0b3954",
          subheadlineSize: "20px",
          subheadlineColor: "#55778a",
          // CTA button spacing
          ctaTopMargin: 24,
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    if (section?.id && ["headline", "subheadline", "body", "imageUrl", "images"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        settings: { ...prev.settings, [key]: value }
      }

      // Update preview immediately with the new state
      if (
        section?.id &&
        [
          "layout",
          "textAlign",
          "headingAlign",
          "subheadingAlign",
          "ctaAlign",
          "overlayOpacity",
          "showCTA",
          "ctaText",
          "ctaAction",
          "ctaTarget",
          "showReviews",
          "images",
          "showBackgroundColor",
          "backgroundColor",
          "headlineSize",
          "headlineColor",
          "subheadlineSize",
          "subheadlineColor",
          "ctaTopMargin"
        ].includes(key)
      ) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updatedFormData
        })
      }

      return updatedFormData
    })
  }

  // Close button - discard changes and close immediately
  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  // Confirm save from modal
  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    if (validateForm()) {
      onSave({
        layout: section?.layout || "sub_hero",
        ...formData
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.headline.trim()) {
      newErrors.headline = "Headline is required"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      setShowConfirmModal(true)
    }
  }

  const handleMultipleImagesUpload = async (res) => {
    const files = res.filesUploaded || []

    if (files.length === 0) {
      showToast({
        type: "error",
        content: "No files were uploaded. Please try again."
      })
      return
    }

    try {
      const results = await Promise.all(
        files.map((file) =>
          uploadImage({
            handle: file.handle,
            description: "sub_hero_section_image",
            provider: "filestack"
          })
        )
      )

      const successfulUploads = results
        .map((r) => r?.data?.uploadImage)
        .filter((u) => u && u.result === "success")
        .map((u) => ({ url: u.photoUrl, altText: u.altText || "Hero image", type: "image" }))

      if (successfulUploads.length > 0) {
        const currentImages = formData.settings.images || []
        const newImages = [...currentImages, ...successfulUploads]
        handleSettingsChange("images", newImages)

        // Update imageUrl to match the first image in the array (only if no images existed before)
        if (currentImages.length === 0 && newImages.length > 0) {
          const newImageUrl = newImages[0].url
          handleChange("imageUrl", newImageUrl)
        }

        // Update preview immediately when adding images
        if (section?.id) {
          const updatedImageUrl =
            currentImages.length === 0 && newImages.length > 0 ? newImages[0].url : formData.imageUrl
          handleUpdateSectionPreview(section.id, {
            ...section,
            ...formData,
            imageUrl: updatedImageUrl,
            settings: { ...formData.settings, images: newImages }
          })
        }

        showToast(`${successfulUploads.length} image${successfulUploads.length > 1 ? "s" : ""} uploaded successfully!`)
      }

      const failures = results.filter((r) => r?.data?.uploadImage?.result !== "success")
      if (failures.length > 0) {
        showToast({
          type: "error",
          content: "Some images failed to upload. Please try again."
        })
      }
    } catch (err) {
      // Log error for debugging
      // eslint-disable-next-line no-console
      console.error("Upload error:", err)
      showToast({
        type: "error",
        content: "There was an error uploading your images. Please try again later or contact support."
      })
    }
  }

  const handleRemoveMultipleImage = (index) => {
    const currentImages = formData.settings.images || []
    const newImages = currentImages.filter((_, i) => i !== index)
    handleSettingsChange("images", newImages)

    // Update imageUrl to match the first image in the array
    const newImageUrl = newImages.length > 0 ? newImages[0].url : ""
    handleChange("imageUrl", newImageUrl)

    // Update preview immediately when removing images
    if (section?.id) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        imageUrl: newImageUrl,
        settings: { ...formData.settings, images: newImages }
      })
    }

    showToast("Image removed.")
  }

  // Reorder helpers
  const moveImage = (index, delta) => {
    const items = [...(formData.settings.images || [])]
    const newIndex = index + delta
    if (newIndex < 0 || newIndex >= items.length) return
    const [moved] = items.splice(index, 1)
    items.splice(newIndex, 0, moved)

    // Update imageUrl to match the first image in the array
    const newImageUrl = items.length > 0 ? items[0].url : ""

    // Update both settings and imageUrl, then update preview
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        imageUrl: newImageUrl,
        settings: { ...prev.settings, images: items }
      }

      // Update preview immediately with the new state
      if (section?.id) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updatedFormData
        })
      }

      return updatedFormData
    })
  }

  return (
    <>
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Headline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Create a concise headline for sub pages.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Enter your main headline"
                className="box-border flex h-20 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Sub-headline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a brief sub-headline to expand on your main message.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.subheadline}
                onChange={(e) => handleChange("subheadline", e.target.value)}
                placeholder="Supporting text for your headline"
                className="box-border flex h-20 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Text Alignment Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Text Alignment</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Choose how to align your heading and subheading text independently.
              </p>
            </div>
          </div>

          {/* Heading Alignment */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-[#0b3954]">
              <p className="block leading-[20px]">Heading Alignment</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="headingAlign"
                  value="left"
                  checked={formData.settings.headingAlign === "left"}
                  onChange={(e) => handleSettingsChange("headingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Left</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="headingAlign"
                  value="center"
                  checked={formData.settings.headingAlign === "center"}
                  onChange={(e) => handleSettingsChange("headingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Center</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="headingAlign"
                  value="right"
                  checked={formData.settings.headingAlign === "right"}
                  onChange={(e) => handleSettingsChange("headingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Right</p>
                </div>
              </div>
            </div>
          </div>

          {/* Subheading Alignment */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-[#0b3954]">
              <p className="block leading-[20px]">Subheading Alignment</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="subheadingAlign"
                  value="left"
                  checked={formData.settings.subheadingAlign === "left"}
                  onChange={(e) => handleSettingsChange("subheadingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Left</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="subheadingAlign"
                  value="center"
                  checked={formData.settings.subheadingAlign === "center"}
                  onChange={(e) => handleSettingsChange("subheadingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Center</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="subheadingAlign"
                  value="right"
                  checked={formData.settings.subheadingAlign === "right"}
                  onChange={(e) => handleSettingsChange("subheadingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Right</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button Alignment Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">CTA Button Alignment</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Choose how to align the call-to-action button within its container.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
              <input
                type="radio"
                name="ctaAlign"
                value="left"
                checked={formData.settings.ctaAlign === "left"}
                onChange={(e) => handleSettingsChange("ctaAlign", e.target.value)}
                className="relative size-6 shrink-0 accent-[#0d9da4]"
              />
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                <p className="block whitespace-pre leading-[24px]">Left</p>
              </div>
            </div>
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
              <input
                type="radio"
                name="ctaAlign"
                value="center"
                checked={formData.settings.ctaAlign === "center"}
                onChange={(e) => handleSettingsChange("ctaAlign", e.target.value)}
                className="relative size-6 shrink-0 accent-[#0d9da4]"
              />
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                <p className="block whitespace-pre leading-[24px]">Center</p>
              </div>
            </div>
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
              <input
                type="radio"
                name="ctaAlign"
                value="right"
                checked={formData.settings.ctaAlign === "right"}
                onChange={(e) => handleSettingsChange("ctaAlign", e.target.value)}
                className="relative size-6 shrink-0 accent-[#0d9da4]"
              />
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                <p className="block whitespace-pre leading-[24px]">Right</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button Spacing Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">CTA Button Spacing</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Control the vertical spacing between the subheadline text and the call-to-action button.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[20px]">Top Margin</p>
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
                  <p className="block whitespace-pre leading-[20px]">{formData.settings.ctaTopMargin || 24}px</p>
                </div>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="range"
                  min="0"
                  max="80"
                  step="4"
                  value={formData.settings.ctaTopMargin || 24}
                  onChange={(e) => handleSettingsChange("ctaTopMargin", parseInt(e.target.value))}
                  className="relative flex-1 shrink-0 accent-[#0d9da4]"
                />
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[12px] font-normal leading-[0] text-[#55778a]">
                  <p className="block whitespace-pre leading-[18px]">0px</p>
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[12px] font-normal leading-[0] text-[#55778a]">
                  <p className="block whitespace-pre leading-[18px]">80px</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
              <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
                <p className="block leading-[24px]">Call-to-action button</p>
              </div>
              <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
                <p className="block leading-[20px]">
                  Guide visitors to the next step with a clear call-to-action button.
                </p>
              </div>
            </div>

            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
              <div className="relative box-border flex shrink-0 content-center items-center justify-start gap-3 p-0 [flex-flow:wrap]">
                <input
                  type="radio"
                  name="ctaAction"
                  value="page"
                  checked={formData.settings.ctaAction === "page"}
                  onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Page</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="ctaAction"
                  value="service"
                  checked={formData.settings.ctaAction === "service"}
                  onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                  className="relative size-6 shrink-0 accent-[#0d9da4]"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">Service/Package/Event</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                <p className="block whitespace-pre leading-[20px]">Button text</p>
              </div>
            </div>
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.settings.ctaText || "Book a Discovery Call"}
                onChange={(e) => handleSettingsChange("ctaText", e.target.value)}
                placeholder="Book a Discovery Call"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
              />
            </div>
          </div>

          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                <p className="block whitespace-pre leading-[20px]">
                  {formData.settings.ctaAction === "page" ? "Page" : "Service/Package/Event"}
                </p>
              </div>
            </div>
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <select
                value={formData.settings.ctaTarget || ""}
                onChange={(e) => handleSettingsChange("ctaTarget", e.target.value)}
                className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                <option value="">Select</option>
                {formData.settings.ctaAction === "page" ? (
                  <>
                    <option value="about">About</option>
                    <option value="services">Services</option>
                    <option value="contact">Contact</option>
                  </>
                ) : (
                  <>
                    {practice?.services?.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                    {(!practice?.services || practice.services.length === 0) && (
                      <>
                        <option value="discovery-call">Discovery Call</option>
                        <option value="consultation">Consultation</option>
                      </>
                    )}
                  </>
                )}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <ChevronDownIcon className="h-4 w-4 text-[#55778a] opacity-65" />
              </div>
            </div>
            {formData.settings.ctaAction === "service" && (
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                <p className="block whitespace-pre leading-[19.5px]">
                  Go to your <span className="cursor-pointer text-[#0d9da4]">Services</span> to add additional options.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Images</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Upload and organize images for this section. For focused layouts (image-left/right, half-image,
                full-image), only the first image will be displayed.
              </p>
            </div>
          </div>

          {/* Image Organizer - Available for all layouts */}
          <div className="space-y-4">
            {formData.settings.images && formData.settings.images.length > 0 ? (
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
                <div className="grid grid-cols-3 gap-4">
                  {formData.settings.images.map((image, index) => (
                    <div key={index} className="group relative">
                      <div className="overflow-hidden rounded-lg border-2 border-[#c8d6df]">
                        <img
                          src={image.url}
                          alt={image.altText || `Image ${index + 1}`}
                          className="h-24 w-24 object-cover"
                        />
                        {/* Position badge */}
                        <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-xs font-semibold text-white">
                          {index + 1}
                        </div>
                        {/* Remove button */}
                        <div className="absolute right-2 top-2 flex gap-1">
                          <button
                            onClick={() => handleRemoveMultipleImage(index)}
                            className="rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                            disabled={uploadFetching}>
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                        {/* Reorder controls */}
                        <div className="absolute inset-x-0 bottom-1 flex items-center justify-between px-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            className="rounded bg-white/80 px-1 text-xs text-[#0b3954] shadow"
                            onClick={() => moveImage(index, -1)}
                            disabled={index === 0}>
                            ←
                          </button>
                          <button
                            className="rounded bg-white/80 px-1 text-xs text-[#0b3954] shadow"
                            onClick={() => moveImage(index, 1)}
                            disabled={index === (formData.settings.images?.length || 1) - 1}>
                            →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center p-0">
                  <FilePicker
                    as="button"
                    className="font-bold text-[#0d9da4]"
                    onUploadDone={handleMultipleImagesUpload}
                    disabled={uploadFetching}
                    transformations={{}}
                    accept="image/*"
                    multiple={true}
                    maxFiles={10}
                    maxSize={25 * 1024 * 1024}
                    fromSources={["local_file_system", "url", "imagesearch", "googledrive", "dropbox"]}>
                    Add more images
                  </FilePicker>
                </div>
                {/* Layout-specific info */}
                {["image_left", "image_right", "half_image_left", "half_image_right", "full_image"].includes(
                  formData.settings.layout
                ) && (
                  <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
                    <strong>Note:</strong> This layout will use the first image. Other images are available for
                    switching or future use.
                  </div>
                )}
              </div>
            ) : (
              <FilePicker
                as="button"
                onUploadDone={handleMultipleImagesUpload}
                disabled={uploadFetching}
                transformations={{}}
                accept="image/*"
                multiple={true}
                maxFiles={10}
                maxSize={25 * 1024 * 1024}
                fromSources={["local_file_system", "url", "imagesearch", "googledrive", "dropbox"]}
                className="relative box-border flex w-full shrink-0 flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#c8d6df] bg-white px-8 py-10 transition-colors hover:border-[#0d9da4]">
                <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-[#e2f3f4]">
                  <svg className="h-5 w-5 text-[#0d9da4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0d9da4]">
                  <p className="whitespace-pre leading-[24px]">
                    <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload images</span>
                  </p>
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                  <p className="block whitespace-pre leading-[19.5px]">
                    Select multiple images at once. Supported formats: PNG, JPG, SVG (max 25MB each)
                  </p>
                </div>
              </FilePicker>
            )}
          </div>
        </div>

        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Client reviews</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Show your overall star rating from clients.</p>
            </div>
          </div>
          <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
            <Toggle
              checked={formData.settings.showReviews || false}
              onChange={(checked) => handleSettingsChange("showReviews", checked)}
            />
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[24px]">Include review ratings</p>
            </div>
          </div>
        </div>

        {/* Font Settings Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-[#e4eff6] px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Typography</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Customize the fonts and styling of your text content.</p>
            </div>
          </div>

          {/* Headline Font Settings */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-[#0b3954]">
              <p className="block leading-[20px]">Headline</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-[#0b3954]">
                  <p className="block leading-[18px]">Font Size</p>
                </div>
                <select
                  value={formData.settings.headlineSize || "56px"}
                  onChange={(e) => handleSettingsChange("headlineSize", e.target.value)}
                  className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                  <option value="32px">32px</option>
                  <option value="40px">40px</option>
                  <option value="48px">48px</option>
                  <option value="56px">56px</option>
                  <option value="64px">64px</option>
                  <option value="72px">72px</option>
                </select>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-[#0b3954]">
                  <p className="block leading-[18px]">Text Color</p>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                  <input
                    type="color"
                    value={formData.settings.headlineColor || "#0b3954"}
                    onChange={(e) => handleSettingsChange("headlineColor", e.target.value)}
                    className="relative h-10 w-16 shrink-0 cursor-pointer rounded border border-[#c8d6df]"
                  />
                  <input
                    type="text"
                    value={formData.settings.headlineColor || "#0b3954"}
                    onChange={(e) => handleSettingsChange("headlineColor", e.target.value)}
                    placeholder="#0b3954"
                    className="box-border flex h-10 w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Subheadline Font Settings */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-[#0b3954]">
              <p className="block leading-[20px]">Subheadline</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-[#0b3954]">
                  <p className="block leading-[18px]">Font Size</p>
                </div>
                <select
                  value={formData.settings.subheadlineSize || "20px"}
                  onChange={(e) => handleSettingsChange("subheadlineSize", e.target.value)}
                  className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                  <option value="14px">14px</option>
                  <option value="16px">16px</option>
                  <option value="18px">18px</option>
                  <option value="20px">20px</option>
                  <option value="24px">24px</option>
                  <option value="28px">28px</option>
                </select>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-[#0b3954]">
                  <p className="block leading-[18px]">Text Color</p>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                  <input
                    type="color"
                    value={formData.settings.subheadlineColor || "#55778a"}
                    onChange={(e) => handleSettingsChange("subheadlineColor", e.target.value)}
                    className="relative h-10 w-16 shrink-0 cursor-pointer rounded border border-[#c8d6df]"
                  />
                  <input
                    type="text"
                    value={formData.settings.subheadlineColor || "#55778a"}
                    onChange={(e) => handleSettingsChange("subheadlineColor", e.target.value)}
                    placeholder="#55778a"
                    className="box-border flex h-10 w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section background color - Commented out - now using bgLight from theme
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pb-6 pt-0">
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
          <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
            <p className="block leading-[24px]">Section background color</p>
          </div>
          <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
            <p className="block leading-[20px]">Include a background color in this section.</p>
          </div>
        </div>
        <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
          <Toggle
            checked={formData.settings.showBackgroundColor || false}
            onChange={(checked) => handleSettingsChange("showBackgroundColor", checked)}
          />
          <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
            <p className="block whitespace-pre leading-[24px]">Background color</p>
          </div>
        </div>

        {formData.settings.showBackgroundColor && (
          <ColorInput
            label="Background Color"
            value={formData.settings.backgroundColor}
            onChange={(value) => handleSettingsChange("backgroundColor", value)}
            className="mt-4"
          />
        )}
      </div>
      */}

      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

export default SubHeroEditor
